import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";

/**
 * Hook per caricare la landing-config dinamica dal backend
 * e applicare palette + font come CSS variables sul <html>.
 */

const FONT_FAMILIES = {
  "instrument-serif": "'Fraunces', 'Instrument Serif', 'Cormorant Garamond', serif",
  "fraunces": "'Fraunces', 'Cormorant Garamond', serif",
  "cormorant": "'Fraunces', 'Cormorant Garamond', serif",
  "playfair": "'Fraunces', 'Playfair Display', 'Cormorant Garamond', serif",
  "dm-serif-display": "'Fraunces', 'DM Serif Display', 'Cormorant Garamond', serif",
};

// Import lazy dei font extra (se selezionati) via link tag one-time
const FONT_CDN_URLS = {
  "fraunces": "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&display=swap",
  "playfair": "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap",
  "dm-serif-display": "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&display=swap",
};

function ensureFontLoaded(font) {
  const url = FONT_CDN_URLS[font];
  if (!url) return;
  const id = `epicflow-font-${font}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
}

function applyPalette(palette) {
  if (!palette) return;
  const root = document.documentElement;
  if (palette.primary_hsl) root.style.setProperty("--primary", palette.primary_hsl);
  if (palette.secondary_hsl) root.style.setProperty("--secondary", palette.secondary_hsl);
  if (palette.background_hsl) root.style.setProperty("--background", palette.background_hsl);
  if (palette.foreground_hsl) {
    root.style.setProperty("--foreground", palette.foreground_hsl);
    // I componenti UI utilizzano anche --card-foreground / --popover-foreground
    // che di default derivano dal --foreground: li allineiamo esplicitamente per
    // evitare che shadcn-cards restino con il testo del vecchio tema.
    root.style.setProperty("--card-foreground", palette.foreground_hsl);
    root.style.setProperty("--popover-foreground", palette.foreground_hsl);
  }
  if (palette.accent_hsl) root.style.setProperty("--accent", palette.accent_hsl);
  root.style.setProperty("--ring", palette.primary_hsl || root.style.getPropertyValue("--primary"));
}

function applyFont(font) {
  if (!font) return;
  ensureFontLoaded(font);
  const family = FONT_FAMILIES[font] || FONT_FAMILIES["instrument-serif"];
  document.documentElement.style.setProperty("--font-display", family);
  // Injecta override runtime — SIA la classe display sia il body della landing
  const styleId = "epicflow-font-override";
  let el = document.getElementById(styleId);
  if (!el) {
    el = document.createElement("style");
    el.id = styleId;
    document.head.appendChild(el);
  }
  // Applica il font ai titoli (font-serif-display) su tutta l'app;
  // sui contenitori marcati `data-landing="true"` applica ANCHE al body per
  // uniformità stilistica su tutta la landing pubblica.
  el.textContent = `
    .font-serif-display, .font-editorial, .font-unbounded { font-family: ${family} !important; }
    [data-landing="true"], [data-landing="true"] p, [data-landing="true"] span, [data-landing="true"] a, [data-landing="true"] div, [data-landing="true"] li { font-family: ${family}; }
  `;
}

let CACHE = null;
let CACHE_PROMISE = null;

async function fetchConfig() {
  if (CACHE) return CACHE;
  if (CACHE_PROMISE) return CACHE_PROMISE;
  CACHE_PROMISE = api.get("/public/landing-config")
    .then(r => { CACHE = r.data; return CACHE; })
    .catch(() => { CACHE = null; return null; });
  return CACHE_PROMISE;
}

export function useLandingConfig() {
  const [config, setConfig] = useState(CACHE);

  useEffect(() => {
    let cancelled = false;
    fetchConfig().then(c => {
      if (cancelled) return;
      setConfig(c);
      if (c) {
        applyPalette(c.palette);
        applyFont(c.font_display);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Helper accessor con fallback ai default
  const T = useMemo(() => (path, fallback) => {
    if (!config) return fallback;
    const keys = path.split(".");
    let cur = config;
    for (const k of keys) {
      if (cur == null) return fallback;
      cur = cur[k];
    }
    return cur ?? fallback;
  }, [config]);

  return { config, cfg: config, T, ready: config !== null };
}

export function invalidateLandingConfig() {
  CACHE = null;
  CACHE_PROMISE = null;
}

export { FONT_FAMILIES };
